import React, { useState, useCallback } from 'react';
import { toLocalYMD } from '../lib/dates';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, Download, X, Loader2, Users, Briefcase, ArrowRight, Link } from 'lucide-react';
import { customerChanged, jobChanged, Actions } from '../utils/dataSync';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Column definitions for Squeegee-style imports
const CUSTOMER_COLUMNS = {
  required: ['name'],
  optional: ['address', 'email', 'phone', 'zone', 'round', 'notes', 'tags', 'postcode', 'town', 'street']
};

const JOB_COLUMNS = {
  required: ['customer_name'], // Used to match to imported customers
  optional: ['service', 'services', 'title', 'price', 'frequency', 'next_due', 'last_done', 'assignee', 'duration', 'notes', 'address', 'job_address', 'round', 'zone', 'job_state']
};

const SqueegeeImport = ({ onClose, onImportComplete }) => {
  // Step tracking
  const [currentStep, setCurrentStep] = useState(1); // 1 = customers, 2 = jobs, 3 = review, 4 = importing, 5 = complete
  const [showHowTo, setShowHowTo] = useState(false);
  // Combined single-file Squeegee export (one row per job) — server-side import
  const [combinedPreview, setCombinedPreview] = useState(null);
  const [combinedResult, setCombinedResult] = useState(null);
  const [combinedImporting, setCombinedImporting] = useState(false);
  const [assigneeLinks, setAssigneeLinks] = useState({});
  
  // Customer file state
  const [customerFile, setCustomerFile] = useState(null);
  const [customerData, setCustomerData] = useState([]);
  const [customerHeaders, setCustomerHeaders] = useState([]);
  const [customerMapping, setCustomerMapping] = useState({});
  
  // Duplicate tracking
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [duplicateCustomers, setDuplicateCustomers] = useState([]);
  const [newCustomersToImport, setNewCustomersToImport] = useState([]);
  
  // Job file state
  const [jobFile, setJobFile] = useState(null);
  const [jobData, setJobData] = useState([]);
  const [jobHeaders, setJobHeaders] = useState([]);
  const [jobMapping, setJobMapping] = useState({});
  
  // Matching state
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [unmatchedJobs, setUnmatchedJobs] = useState([]);
  
  // Import results
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Parse CSV file - handles multi-line quoted fields properly
  const parseCSV = (text) => {
    // First, parse the entire CSV properly handling multi-line quoted fields
    const rows = [];
    let currentRow = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          currentField += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        currentRow.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        // End of row (skip \r in \r\n)
        if (char === '\r' && nextChar === '\n') {
          i++; // Skip \n
        }
        if (currentField || currentRow.length > 0) {
          currentRow.push(currentField.trim());
          if (currentRow.some(f => f)) { // Only add non-empty rows
            rows.push(currentRow);
          }
          currentRow = [];
          currentField = '';
        }
      } else {
        currentField += char;
      }
    }
    
    // Don't forget the last field/row
    if (currentField || currentRow.length > 0) {
      currentRow.push(currentField.trim());
      if (currentRow.some(f => f)) {
        rows.push(currentRow);
      }
    }
    
    if (rows.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    const csvHeaders = rows[0].map(h => h.toLowerCase().replace(/['"]/g, '').trim());
    
    const data = [];
    for (let i = 1; i < rows.length; i++) {
      const values = rows[i];
      if (values.length > 0) {
        const row = {};
        csvHeaders.forEach((header, idx) => {
          row[header] = (values[idx] || '').trim();
        });
        data.push(row);
      }
    }

    return { headers: csvHeaders, data };
  };

  // Keep parseCSVLine for backwards compatibility but it's no longer used
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    
    return values.map(v => v.replace(/^["']|["']$/g, ''));
  };

  // Auto-map columns with stricter matching
  const autoMapColumns = (headers, columnDefs) => {
    const mapping = {};
    const allColumns = [...columnDefs.required, ...columnDefs.optional];
    
    // Define exact matches and aliases for better mapping - based on Squeegee CSV format
    const columnAliases = {
      // Customer columns - handles both "Name" and "Name_customer" formats
      'name': ['name', 'customer', 'customer_name', 'customername', 'full_name', 'fullname', 'client', 'name_customer', 'namecustomer'],
      'address': ['address', 'full_address', 'fulladdress', 'street_address', 'streetaddress', 'location', 'address_customer', 'addresscustomer'],
      'email': ['email', 'email_address', 'emailaddress', 'e-mail', 'e_mail', 'email_customer', 'emailcustomer'],
      'phone': ['phone', 'telephone', 'tel', 'mobile', 'cell', 'phone_number', 'phonenumber', 'phone_customer', 'phonecustomer'],
      'zone': ['zone', 'area', 'region', 'territory', 'rounds', 'round', 'route', 'routes'],
      'notes': ['notes', 'note', 'comments', 'comment'],
      'postcode': ['postcode', 'post_code', 'zip', 'zipcode', 'zip_code', 'postal'],
      'town': ['town', 'city', 'suburb'],
      'street': ['street', 'street_name', 'streetname', 'road'],
      'tags': ['tags', 'tag', 'labels', 'label', 'categories'],
      'balance': ['balance', 'balance_customer', 'balancecustomer', 'balance_job', 'balancejob'],
      
      // Job columns - handles both "Name" and "Name_job" formats
      'customer_name': ['name', 'customer_name', 'customername', 'customer', 'client', 'name_job', 'namejob', 'name_customer', 'namecustomer'],
      'service': ['services', 'service', 'service_type', 'servicetype', 'job_type', 'jobtype'],
      'services': ['services', 'service_types', 'job_types'],
      'title': ['title', 'job_title', 'jobtitle', 'description'],
      'price': ['price', 'amount', 'cost', 'fee', 'charge', 'rate'],
      'frequency': ['frequency', 'freq', 'schedule', 'interval', 'recurring'],
      'next_due': ['next_due', 'nextdue', 'next_date', 'nextdate', 'due_date', 'duedate', 'next due'],
      'last_done': ['last_done', 'lastdone', 'last_date', 'lastdate', 'completed', 'last done'],
      'assignee': ['assignee', 'assigned', 'worker', 'staff', 'employee', 'technician'],
      'duration': ['duration', 'time', 'estimated_time', 'job_time'],
      'job_address': ['job_address', 'jobaddress', 'job address', 'address_job', 'addressjob'],
      'job_state': ['job_state', 'jobstate', 'job state', 'state', 'status']
    };
    
    headers.forEach(header => {
      const normalizedHeader = header.toLowerCase().replace(/[_\s-]/g, '').replace(/['"]/g, '');
      
      // Find the best match using aliases
      for (const col of allColumns) {
        const aliases = columnAliases[col] || [col];
        const matched = aliases.some(alias => {
          const normalizedAlias = alias.toLowerCase().replace(/[_\s-]/g, '');
          // Exact match or alias exact match
          return normalizedHeader === normalizedAlias;
        });
        
        if (matched && !mapping[col]) {
          mapping[col] = header;
          console.log(`Column mapping: "${header}" -> ${col}`);
          break;
        }
      }
    });
    
    console.log('Final mapping:', mapping);
    return mapping;
  };

  // Check for duplicate customers
  const checkForDuplicates = async (data, mapping) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      
      if (!token) {
        console.log('No auth token, skipping duplicate check');
        setNewCustomersToImport(data);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/customers?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Clone response to avoid "body stream already read" error
      const responseClone = response.clone();
      
      if (response.ok) {
        let existingData;
        try {
          existingData = await response.json();
        } catch (jsonErr) {
          // If JSON parsing fails, try clone
          existingData = await responseClone.json();
        }
        const existing = Array.isArray(existingData) ? existingData : (existingData.customers || []);
        setExistingCustomers(existing);

        // Create a set of existing customer identifiers (name + address normalized)
        const existingSet = new Set();
        existing.forEach(c => {
          const key = `${(c.name || '').toLowerCase().trim()}|${(c.address || '').toLowerCase().trim()}`;
          existingSet.add(key);
          // Also add just by name for fuzzy matching
          existingSet.add(`name:${(c.name || '').toLowerCase().trim()}`);
        });

        const nameCol = mapping.name || 'name';
        const addressCol = mapping.address || 'address';
        
        const dupes = [];
        const newOnes = [];

        data.forEach((row, idx) => {
          const name = (row[nameCol] || '').toLowerCase().trim();
          const address = (row[addressCol] || '').toLowerCase().trim();
          const key = `${name}|${address}`;
          const nameKey = `name:${name}`;

          if (existingSet.has(key) || existingSet.has(nameKey)) {
            dupes.push({ ...row, _rowIndex: idx });
          } else {
            newOnes.push({ ...row, _rowIndex: idx });
            // Add to set to catch duplicates within the same file
            existingSet.add(key);
            existingSet.add(nameKey);
          }
        });

        setDuplicateCustomers(dupes);
        setNewCustomersToImport(newOnes);
      } else {
        console.error('Duplicate check failed:', response.status);
        // If duplicate check fails, treat all as new
        setNewCustomersToImport(data);
      }
    } catch (err) {
      console.error('Error checking duplicates:', err);
      // If duplicate check fails, treat all as new
      setNewCustomersToImport(data);
    }
  };

  // Handle customer file upload
  const handleCustomerFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setCustomerFile(selectedFile);
    setError(null);
    setIsProcessing(true);

    try {
      const text = await selectedFile.text();

      // Combined Squeegee export (customers + jobs in one file) — the
      // backend handles grouping, multi-job customers and scheduling.
      if (text.slice(0, 2000).includes('Job - Job Ref')) {
        const token = localStorage.getItem('streakfree_token');
        const fd = new FormData();
        fd.append('file', selectedFile);
        const res = await fetch(`${API_URL}/api/import/squeegee/preview`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || 'Could not analyse Squeegee file');
        }
        const previewData = await res.json();
        setCombinedPreview(previewData);
        // Pre-select suggested user matches for each CSV assignee
        const links = {};
        (previewData.assignees || []).forEach(a => {
          if (a.matched_user_id) links[a.name] = a.matched_user_id;
        });
        setAssigneeLinks(links);
        setIsProcessing(false);
        return;
      }

      const { headers, data } = parseCSV(text);
      
      setCustomerHeaders(headers);
      setCustomerData(data);
      const mapping = autoMapColumns(headers, CUSTOMER_COLUMNS);
      setCustomerMapping(mapping);
      
      // Check for duplicates
      await checkForDuplicates(data, mapping);
      
      setIsProcessing(false);
    } catch (err) {
      setError(err.message);
      setIsProcessing(false);
    }
  };

  // Handle job file upload
  const handleJobFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setJobFile(selectedFile);
    setError(null);
    setIsProcessing(true);

    try {
      const text = await selectedFile.text();
      const { headers, data } = parseCSV(text);
      
      setJobHeaders(headers);
      setJobData(data);
      setJobMapping(autoMapColumns(headers, JOB_COLUMNS));
      setIsProcessing(false);
    } catch (err) {
      setError(err.message);
      setIsProcessing(false);
    }
  };

  // Match jobs to customers
  const matchJobsToCustomers = useCallback(() => {
    if (!customerMapping.name || !jobMapping.customer_name) return;

    const customerNameCol = customerMapping.name;
    const jobCustomerCol = jobMapping.customer_name;

    // Create a set of customer names (normalized)
    const customerNames = new Set();
    customerData.forEach(row => {
      const name = (row[customerNameCol] || '').toLowerCase().trim();
      if (name) customerNames.add(name);
    });

    // Match jobs
    const matched = [];
    const unmatched = [];

    jobData.forEach((job, idx) => {
      const jobCustomerName = (job[jobCustomerCol] || '').toLowerCase().trim();
      if (customerNames.has(jobCustomerName)) {
        matched.push({ ...job, _originalIndex: idx });
      } else {
        unmatched.push({ ...job, _originalIndex: idx, _customerName: job[jobCustomerCol] });
      }
    });

    setMatchedJobs(matched);
    setUnmatchedJobs(unmatched);
  }, [customerData, jobData, customerMapping, jobMapping]);

  // Go to review step
  const goToReview = () => {
    matchJobsToCustomers();
    setCurrentStep(3);
  };

  // Import data
  const handleImport = async () => {
    setCurrentStep(4);
    setError(null);

    try {
      const token = localStorage.getItem('streakfree_token');

      // Step 1: Import only NEW customers (skip duplicates)
      const dataToImport = newCustomersToImport.length > 0 ? newCustomersToImport : customerData;
      const customers = dataToImport.map(row => {
        const customer = {
          name: row[customerMapping.name] || ''
        };

        if (customerMapping.address) customer.address = row[customerMapping.address];
        if (customerMapping.email) customer.email = row[customerMapping.email];
        if (customerMapping.phone) customer.phone = row[customerMapping.phone];
        // Zone from "Rounds" or "Zone" column - this is the round/area badge
        if (customerMapping.zone) {
          const zoneValue = row[customerMapping.zone];
          if (zoneValue) {
            customer.zone = zoneValue;
            customer.round = zoneValue;  // Also set round for compatibility
          }
        }
        if (customerMapping.notes) customer.notes = row[customerMapping.notes];
        if (customerMapping.postcode) {
          customer.address = `${customer.address || ''}, ${row[customerMapping.postcode]}`.trim().replace(/^,\s*/, '');
        }
        if (customerMapping.town) {
          customer.address = `${customer.address || ''}, ${row[customerMapping.town]}`.trim().replace(/^,\s*/, '');
        }
        if (customerMapping.street) {
          const street = row[customerMapping.street];
          customer.address = street ? `${street}${customer.address ? ', ' + customer.address : ''}` : customer.address;
        }

        return customer;
      }).filter(c => c.name);

      // Import customers in batches
      const batchSize = 100;
      let totalCustomersCreated = 0;
      const importedCustomerMap = new Map(); // name -> customer data

      for (let i = 0; i < customers.length; i += batchSize) {
        const batch = customers.slice(i, i + batchSize);
        
        const response = await fetch(`${API_URL}/api/customers/bulk`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ customers: batch, include_jobs: false })
        });

        if (response.ok) {
          let result;
          try {
            result = await response.json();
          } catch (jsonErr) {
            console.error('JSON parse error for customers batch:', jsonErr);
            result = {};
          }
          totalCustomersCreated += result.created_count || 0;
          
          // Track imported customers for job matching
          if (result.created) {
            result.created.forEach(c => {
              importedCustomerMap.set(c.name.toLowerCase().trim(), c);
            });
          }
        } else {
          console.error('Customers batch failed:', response.status);
        }
      }

      // IMPORTANT: Also fetch existing customers so we can match jobs to them
      // This handles the case where customers were already imported (duplicates skipped)
      // Fetch ALL customers to ensure we don't miss any matches
      let allCustomers = [];
      
      try {
        // First get the count
        const countResponse = await fetch(`${API_URL}/api/customers/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        let totalCount = 1000; // Default fallback
        if (countResponse.ok) {
          const countData = await countResponse.json();
          totalCount = countData.count || countData.total || 1000;
        }
        
        // Fetch all customers
        const existingResponse = await fetch(`${API_URL}/api/customers?limit=${Math.max(totalCount, 2000)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (existingResponse.ok) {
          let data;
          try {
            data = await existingResponse.json();
          } catch (jsonErr) {
            console.error('JSON parse error:', jsonErr);
            data = [];
          }
          allCustomers = Array.isArray(data) ? data : (data.customers || []);
          console.log(`Fetched ${allCustomers.length} customers from database`);
        } else {
          console.error('Failed to fetch customers:', existingResponse.status);
          // Don't set error here - continue with imported customers only
        }
      } catch (fetchErr) {
        console.error('Error fetching customers:', fetchErr);
        // Don't set error here - continue with imported customers only
      }
      
      allCustomers.forEach(c => {
        const key = (c.name || '').toLowerCase().trim();
        if (!importedCustomerMap.has(key)) {
          importedCustomerMap.set(key, c);
        }
      });
      
      console.log(`Customer map now has ${importedCustomerMap.size} entries`);
      
      // Debug: Show some sample keys from the map
      const sampleKeys = Array.from(importedCustomerMap.keys()).slice(0, 5);
      console.log('Sample customer map keys:', sampleKeys);

      // Step 2: Import jobs for matched customers
      // Re-match ALL jobs against the database customers (not just preview matches)
      let totalJobsCreated = 0;
      let totalJobsSkippedDuplicates = 0;
      const jobsToCreate = [];
      let matchedCount = 0;
      let unmatchedCount = 0;

      console.log(`Processing ${jobData.length} jobs from CSV`);
      console.log(`Customer map size: ${importedCustomerMap.size}`);
      console.log('Job mapping:', jobMapping);
      
      // Debug: Show first job row fully
      if (jobData.length > 0) {
        console.log('First job row keys:', Object.keys(jobData[0]));
        console.log('First job row:', jobData[0]);
        console.log('Mapped values for first job:');
        console.log('  customer_name col:', jobMapping.customer_name, '-> value:', jobData[0][jobMapping.customer_name]);
        console.log('  price col:', jobMapping.price, '-> value:', jobData[0][jobMapping.price]);
        console.log('  frequency col:', jobMapping.frequency, '-> value:', jobData[0][jobMapping.frequency]);
        console.log('  next_due col:', jobMapping.next_due, '-> value:', jobData[0][jobMapping.next_due]);
        console.log('  last_done col:', jobMapping.last_done, '-> value:', jobData[0][jobMapping.last_done]);
      }
      
      // Debug: Show first few customer names in map
      const mapKeys = Array.from(importedCustomerMap.keys()).slice(0, 10);
      console.log('Sample customer names in map:', mapKeys);
      
      // Debug: Show first few job customer names
      const jobNames = jobData.slice(0, 10).map(j => (j[jobMapping.customer_name] || '').toLowerCase().trim());
      console.log('Sample job customer names:', jobNames);

      // Use ALL job data, not just matchedJobs (which was preview-only)
      jobData.forEach((jobRow, idx) => {
        // Get next_due and last_done values
        let nextDueValue = jobRow[jobMapping.next_due]?.trim() || '';
        const lastDoneValue = jobRow[jobMapping.last_done]?.trim() || '';
        const frequencyValue = jobRow[jobMapping.frequency]?.trim() || '';
        
        // If no Next Due but has Last Done and Frequency, calculate Next Due
        if (!nextDueValue && lastDoneValue && frequencyValue) {
          try {
            // Parse frequency to get weeks interval
            const freqLower = frequencyValue.toLowerCase();
            let weeksInterval = null;
            const weeksMatch = freqLower.match(/(\d+)\s*week/);
            if (weeksMatch) {
              weeksInterval = parseInt(weeksMatch[1]);
            } else if (freqLower.includes('weekly')) {
              weeksInterval = 1;
            } else if (freqLower.includes('fortnightly')) {
              weeksInterval = 2;
            }
            
            if (weeksInterval) {
              // Parse last_done date and add weeks
              let lastDoneDate;
              if (lastDoneValue.includes('/')) {
                const [d, m, y] = lastDoneValue.split('/');
                lastDoneDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
              } else {
                lastDoneDate = new Date(lastDoneValue);
              }
              
              if (!isNaN(lastDoneDate.getTime())) {
                const nextDueDate = new Date(lastDoneDate);
                nextDueDate.setDate(nextDueDate.getDate() + (weeksInterval * 7));
                nextDueValue = toLocalYMD(nextDueDate);
                if (idx < 5) console.log(`Calculated next_due for row ${idx}: ${lastDoneValue} + ${weeksInterval} weeks = ${nextDueValue}`);
              }
            }
          } catch (e) {
            console.log(`Error calculating next_due for row ${idx}:`, e);
          }
        }
        
        // Skip jobs that still have no Next Due (can't be scheduled)
        if (!nextDueValue) {
          if (idx < 10) console.log(`Skipping job row ${idx} - no Next Due date and couldn't calculate`);
          return;
        }
        
        const customerName = (jobRow[jobMapping.customer_name] || '').toLowerCase().trim();
        
        if (!customerName) {
          if (idx < 5) console.log(`Job ${idx}: Empty customer name`);
          return;
        }
        
        const customer = importedCustomerMap.get(customerName);
        
        if (!customer) {
          unmatchedCount++;
          if (unmatchedCount <= 5) {
            console.log(`No match for: "${customerName}"`);
          }
          return;
        }
        
        matchedCount++;
        
        // Skip inactive jobs (Job State = inactive from Squeegee)
        const jobState = (jobRow[jobMapping.job_state] || '').toLowerCase().trim();
        if (jobState === 'inactive') {
          if (idx < 5) console.log(`Skipping job ${idx} - job_state is inactive`);
          return;
        }
        
        // Get round from job CSV, fall back to customer zone
        const jobRound = jobRow[jobMapping.round] || jobRow[jobMapping.zone] || customer.zone || '';
        // Get address from job CSV (Job Address column M), fall back to customer address
        const jobAddress = jobRow[jobMapping.job_address] || jobRow[jobMapping.address] || customer.address || '';
        
        const jobDoc = {
          customer_id: customer.id,
          customer_name: customer.name,
          service: jobRow[jobMapping.service] || jobRow[jobMapping.services] || jobRow[jobMapping.title] || 'Window Clean',
          price: parseFloat((jobRow[jobMapping.price] || '0').replace('£', '').replace(',', '')) || 0,
          frequency: jobRow[jobMapping.frequency] || '',
          assignee: jobRow[jobMapping.assignee] || '',
          notes: jobRow[jobMapping.notes] || '',
          address: jobAddress,
          round: jobRound,
          zone: jobRound
        };

        // Use the nextDueValue we calculated earlier (either from CSV or from last_done + frequency)
        if (nextDueValue) {
          let parsed;
          
          // Try DD/MM/YYYY format first (Squeegee format)
          const ddmmyyyy = nextDueValue.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
          if (ddmmyyyy) {
            const [, day, month, year] = ddmmyyyy;
            parsed = new Date(year, month - 1, day);
          } else {
            // Fall back to standard parsing (YYYY-MM-DD or other)
            parsed = new Date(nextDueValue);
          }
          
          if (parsed && !isNaN(parsed.getTime())) {
            jobDoc.scheduled_date = toLocalYMD(parsed);
            jobDoc.next_due = toLocalYMD(parsed);
          }
        }
        
        // Parse last_done date
        if (lastDoneValue) {
          const ddmmyyyy = lastDoneValue.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
          if (ddmmyyyy) {
            const [, day, month, year] = ddmmyyyy;
            const parsed = new Date(year, month - 1, day);
            if (!isNaN(parsed.getTime())) {
              jobDoc.last_done = toLocalYMD(parsed);
            }
          } else {
            const parsed = new Date(lastDoneValue);
            if (!isNaN(parsed.getTime())) {
              jobDoc.last_done = toLocalYMD(parsed);
            } else {
              jobDoc.last_done = lastDoneValue;
            }
          }
        }

        jobsToCreate.push(jobDoc);
      });

      console.log(`Job matching complete: ${matchedCount} matched, ${unmatchedCount} unmatched`);

      // Import jobs in batches
      console.log(`Jobs to create: ${jobsToCreate.length}`);
      
      for (let i = 0; i < jobsToCreate.length; i += batchSize) {
        const batch = jobsToCreate.slice(i, i + batchSize);
        
        console.log(`Sending batch ${i/batchSize + 1} with ${batch.length} jobs`);
        
        const response = await fetch(`${API_URL}/api/jobs/bulk-create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(batch)
        });

        if (response.ok) {
          let result;
          try {
            result = await response.json();
          } catch (jsonErr) {
            console.error('JSON parse error for jobs batch:', jsonErr);
            result = {};
          }
          console.log('Batch result:', result);
          totalJobsCreated += result.created_count || result.created?.length || 0;
          // Track duplicate jobs that were skipped
          if (result.skipped_duplicates) {
            totalJobsSkippedDuplicates += result.skipped_duplicates;
          }
        } else {
          console.error('Jobs batch failed:', response.status);
        }
      }

      setImportResult({
        customersCreated: totalCustomersCreated,
        jobsCreated: totalJobsCreated,
        jobsUnmatched: unmatchedJobs.length,
        jobsSkippedDuplicates: totalJobsSkippedDuplicates
      });
      setCurrentStep(5);

      // Emit bulk changes to update ALL dependent areas
      if (totalCustomersCreated > 0) {
        customerChanged(Actions.BULK_CREATE, { count: totalCustomersCreated });
      }
      if (totalJobsCreated > 0) {
        jobChanged(Actions.BULK_CREATE, { count: totalJobsCreated });
      }

      if (onImportComplete) {
        onImportComplete(totalCustomersCreated);
      }
    } catch (err) {
      setError(err.message);
      setCurrentStep(3);
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[
        { num: 1, label: 'Customers' },
        { num: 2, label: 'Jobs' },
        { num: 3, label: 'Review' },
        { num: 4, label: 'Import' }
      ].map((step, idx) => (
        <React.Fragment key={step.num}>
          <div className={`flex items-center gap-2 ${currentStep >= step.num ? 'text-emerald-600' : 'text-zinc-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep > step.num ? 'bg-emerald-500 text-white' :
              currentStep === step.num ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500' :
              'bg-zinc-100 text-zinc-400'
            }`}>
              {currentStep > step.num ? <CheckCircle size={16} /> : step.num}
            </div>
            <span className="text-sm font-medium hidden sm:inline">{step.label}</span>
          </div>
          {idx < 3 && <ArrowRight size={16} className="text-zinc-300" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full modal-shell-h overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-zinc-900">Import from Squeegee/Other CRM</h2>
            <p className="text-sm text-zinc-500 mt-1">Upload your customers and jobs CSV files</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-zinc-500" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 pt-4">
          {renderStepIndicator()}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 text-red-700 rounded-xl p-4 flex items-start gap-3 mb-4">
              <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* How to get your files from Squeegee */}
          {!combinedResult && currentStep <= 2 && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl mb-4 overflow-hidden" data-testid="squeegee-howto-panel">
              <button
                onClick={() => setShowHowTo(!showHowTo)}
                className="w-full px-4 py-3 flex items-center justify-between text-left"
                data-testid="squeegee-howto-toggle"
              >
                <span className="text-sm font-semibold text-sky-900">📥 How to get your 2 files from Squeegee</span>
                <span className="text-sky-500 text-xs">{showHowTo ? '▾ Hide' : '▸ Show me'}</span>
              </button>
              {showHowTo && (
                <div className="px-4 pb-4 space-y-2.5" data-testid="squeegee-howto-steps">
                  <p className="text-xs text-sky-800">You need <strong>2 CSV files</strong> from Squeegee: your <strong>Active Customers</strong> and your <strong>All Job Data</strong>. Here's how:</p>
                  {[
                    <>In Squeegee, tap the <strong>☰ menu</strong> (three horizontal bars, top left)</>,
                    <>Scroll down and tap <strong>Reporting</strong></>,
                    <>Tap <strong>Active Customers</strong> — Squeegee starts building the report. When it's done, tap <strong>Download Report</strong> and save it as <strong>"Active Customers"</strong></>,
                    <>Go back to <strong>Reporting</strong> and tap <strong>All Job Data</strong></>,
                    <>When it's done, tap the <strong>⋮ three dots</strong> (top right) → <strong>Download</strong> — save it as <strong>"All Job Data"</strong></>,
                    <>Upload both files below — customers first, then jobs</>,
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-sky-200 text-sky-800 flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span className="text-xs text-sky-900 leading-relaxed">{step}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Combined single-file Squeegee export — server-side import */}
          {combinedPreview && !combinedResult && (
            <div data-testid="synccentre-squeegee-preview">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                <p className="font-semibold text-emerald-900">Full Squeegee export detected ✓</p>
                <p className="text-sm text-emerald-700 mt-1">One file with customers and jobs together — no second file needed. Prices, frequencies, due dates, rounds and assignees all come across, and map pins are added automatically.</p>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-zinc-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-semibold text-zinc-900">{combinedPreview.customers}</p>
                  <p className="text-sm text-zinc-500">Customers</p>
                </div>
                <div className="bg-zinc-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-semibold text-zinc-900">{combinedPreview.jobs}</p>
                  <p className="text-sm text-zinc-500">Jobs</p>
                </div>
                <div className="bg-zinc-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-semibold text-zinc-900">{combinedPreview.jobs_without_due_date}</p>
                  <p className="text-sm text-zinc-500">Ad hoc (no due date)</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center" data-testid="preview-done-visits">
                  <p className="text-2xl font-semibold text-emerald-700">{combinedPreview.done_visits ?? 0}</p>
                  <p className="text-sm text-emerald-600">Past visits shown as done</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center" data-testid="preview-done-today">
                  <p className="text-2xl font-semibold text-emerald-700">{combinedPreview.done_today ?? 0}</p>
                  <p className="text-sm text-emerald-600">Done today</p>
                </div>
                <div className="bg-zinc-50 rounded-xl p-4 text-center" data-testid="preview-gc-stripe">
                  <p className="text-2xl font-semibold text-zinc-900">{(combinedPreview.gocardless_customers ?? 0)} / {(combinedPreview.stripe_customers ?? 0)}</p>
                  <p className="text-sm text-zinc-500">GoCardless / Stripe</p>
                </div>
              </div>
              {combinedPreview.multi_job_customers?.length > 0 && (
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 mb-4">
                  <p className="font-semibold text-sky-800 text-sm mb-1">Customers with multiple jobs</p>
                  {combinedPreview.multi_job_customers.map((m, i) => (
                    <p key={i} className="text-sm text-sky-700">{m.name} — {m.jobs} jobs ({m.addresses.join(' • ')})</p>
                  ))}
                </div>
              )}
              {combinedPreview.assignees?.length > 0 && (
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 mb-4" data-testid="assignee-linking-section">
                  <p className="font-semibold text-violet-800 text-sm mb-1">Link assignees to users</p>
                  <p className="text-xs text-violet-600 mb-2">Each worker only sees jobs for the assignees linked to them. Unlinked assignees' jobs will only be visible to the owner.</p>
                  {combinedPreview.assignees.map((a) => (
                    <div key={a.name} className="flex items-center gap-2 py-1.5">
                      <p className="text-sm text-violet-900 flex-1 truncate">
                        <span className="font-medium">{a.name}</span>
                        <span className="text-violet-500"> — {a.jobs} jobs</span>
                      </p>
                      <select
                        value={assigneeLinks[a.name] || ''}
                        onChange={(e) => setAssigneeLinks(prev => ({ ...prev, [a.name]: e.target.value }))}
                        data-testid={`assignee-link-${a.name.replace(/\s+/g, '-').toLowerCase()}`}
                        className={`text-sm border rounded-lg px-2 py-1.5 bg-white max-w-[200px] ${assigneeLinks[a.name] ? 'border-violet-300 text-violet-900' : 'border-amber-400 text-amber-700'}`}
                      >
                        <option value="">⚠ Not linked</option>
                        {(combinedPreview.team_users || []).map(u => (
                          <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                        ))}
                      </select>
                      {a.matched_user_id && assigneeLinks[a.name] === a.matched_user_id && (
                        <span className="text-[10px] text-emerald-600 font-medium whitespace-nowrap">already linked ✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {combinedPreview.customers_with_balance?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  <p className="font-semibold text-amber-800 text-sm mb-1">Balances carried over</p>
                  {combinedPreview.customers_with_balance.map((b, i) => (
                    <p key={i} className="text-sm text-amber-700">{b.name} — £{b.balance.toFixed(2)}</p>
                  ))}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => { setCombinedPreview(null); setCustomerFile(null); }}
                  className="flex-1 py-3 border border-zinc-200 rounded-xl text-zinc-700 font-medium hover:bg-zinc-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setCombinedImporting(true);
                    setError(null);
                    try {
                      const token = localStorage.getItem('streakfree_token');
                      const fd = new FormData();
                      fd.append('file', customerFile);
                      const chosenLinks = Object.fromEntries(Object.entries(assigneeLinks).filter(([, v]) => v));
                      if (Object.keys(chosenLinks).length > 0) {
                        fd.append('assignee_links', JSON.stringify(chosenLinks));
                      }
                      const res = await fetch(`${API_URL}/api/import/squeegee`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: fd
                      });
                      if (!res.ok) {
                        const d = await res.json().catch(() => ({}));
                        throw new Error(d.detail || 'Import failed');
                      }
                      setCombinedResult(await res.json());
                      if (onImportComplete) onImportComplete();
                    } catch (err) {
                      setError(err.message);
                    } finally {
                      setCombinedImporting(false);
                    }
                  }}
                  disabled={combinedImporting}
                  data-testid="synccentre-squeegee-import-btn"
                  className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50"
                >
                  {combinedImporting ? 'Importing…' : `Import ${combinedPreview.customers} Customers & ${combinedPreview.jobs} Jobs`}
                </button>
              </div>
            </div>
          )}

          {combinedResult && (
            <div className="text-center py-8" data-testid="synccentre-squeegee-complete">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-900">Import Complete!</h3>
              <p className="text-zinc-600 mt-2">{combinedResult.created_customers} customers and {combinedResult.created_jobs} jobs imported</p>
              {(combinedResult.done_visits_created ?? 0) > 0 && (
                <p className="text-sm text-emerald-700 mt-1" data-testid="result-done-visits">
                  ✓ {combinedResult.done_visits_created} past visits marked as done{combinedResult.done_today ? ` (${combinedResult.done_today} today)` : ''} — no invoices, no customer contact
                </p>
              )}
              {(combinedResult.assignees_linked ?? 0) > 0 && (
                <p className="text-sm text-emerald-700 mt-1" data-testid="result-assignees-linked">
                  ✓ {combinedResult.assignees_linked} assignee{combinedResult.assignees_linked > 1 ? 's' : ''} linked to users
                </p>
              )}
              <p className="text-sm text-zinc-400 mt-2">📍 Map pins are being added in the background — they'll appear over the next few minutes.</p>
              <button onClick={onClose} className="mt-6 px-8 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700">Done</button>
            </div>
          )}

          {/* Step 1: Upload Customers CSV */}
          {!combinedPreview && !combinedResult && currentStep === 1 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users size={32} className="text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900">Step 1: Upload Active Customers</h3>
                <p className="text-sm text-zinc-500 mt-1">Upload your customer list CSV file</p>
              </div>

              {!customerFile ? (
                <div className="border-2 border-dashed border-zinc-200 rounded-xl p-8 text-center hover:border-emerald-300 transition-colors">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCustomerFileChange}
                    className="hidden"
                    id="customer-csv-upload"
                  />
                  <label htmlFor="customer-csv-upload" className="cursor-pointer">
                    <Upload size={48} className="mx-auto text-zinc-300 mb-4" />
                    <p className="text-zinc-600 font-medium">Drop your Customers CSV here or click to browse</p>
                    <p className="text-sm text-zinc-400 mt-1">Required columns: name | Optional: address, email, phone, zone</p>
                  </label>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Duplicate Warning */}
                  {duplicateCustomers.length > 0 && (
                    <div className={`p-4 rounded-xl flex items-start gap-3 ${
                      duplicateCustomers.length >= customerData.length * 0.5 
                        ? 'bg-red-50 border border-red-200' 
                        : 'bg-amber-50 border border-amber-200'
                    }`}>
                      <AlertCircle className={`flex-shrink-0 mt-0.5 ${
                        duplicateCustomers.length >= customerData.length * 0.5 ? 'text-red-500' : 'text-amber-500'
                      }`} size={20} />
                      <div>
                        <h4 className={`font-semibold ${
                          duplicateCustomers.length >= customerData.length * 0.5 ? 'text-red-800' : 'text-amber-800'
                        }`}>
                          {duplicateCustomers.length >= customerData.length * 0.5 
                            ? 'Most customers already exist!' 
                            : 'Some customers already exist'}
                        </h4>
                        <p className={`text-sm mt-1 ${
                          duplicateCustomers.length >= customerData.length * 0.5 ? 'text-red-700' : 'text-amber-700'
                        }`}>
                          {duplicateCustomers.length} of {customerData.length} customers already exist in your account and will be skipped.
                          Only {newCustomersToImport.length} new customer{newCustomersToImport.length !== 1 ? 's' : ''} will be imported.
                        </p>
                        {duplicateCustomers.length >= customerData.length * 0.5 && (
                          <p className="text-sm text-red-600 mt-2">
                            This usually means you've already imported this file. Check your existing customers before proceeding.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl">
                    <FileText className="text-emerald-600" />
                    <div className="flex-1">
                      <p className="font-medium text-emerald-800">{customerFile.name}</p>
                      <p className="text-sm text-emerald-600">
                        {customerData.length} customers found
                        {duplicateCustomers.length > 0 && (
                          <span className="text-amber-600"> ({newCustomersToImport.length} new, {duplicateCustomers.length} existing)</span>
                        )}
                      </p>
                    </div>
                    <CheckCircle className="text-emerald-500" />
                  </div>

                  {/* Column Mapping */}
                  <div className="bg-zinc-50 rounded-xl p-4">
                    <h4 className="font-medium text-zinc-900 mb-3">Column Mapping</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {['name', 'address', 'email', 'phone', 'round', 'notes'].map(col => (
                        <div key={col} className="flex items-center gap-2">
                          <label className="w-20 text-sm font-medium text-zinc-700">
                            {col}{CUSTOMER_COLUMNS.required.includes(col) && <span className="text-red-500">*</span>}
                          </label>
                          <select
                            value={customerMapping[col] || ''}
                            onChange={(e) => setCustomerMapping({...customerMapping, [col]: e.target.value})}
                            className="flex-1 px-3 py-1.5 border border-zinc-200 rounded-lg text-sm"
                          >
                            <option value="">-- Not mapped --</option>
                            {customerHeaders.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="overflow-x-auto border border-zinc-200 rounded-xl">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Name</th>
                          <th className="px-4 py-2 text-left">Address</th>
                          <th className="px-4 py-2 text-left">Phone</th>
                          <th className="px-4 py-2 text-left">Round</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerData.slice(0, 3).map((row, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-2">{row[customerMapping.name] || '-'}</td>
                            <td className="px-4 py-2">{row[customerMapping.address] || '-'}</td>
                            <td className="px-4 py-2">{row[customerMapping.phone] || '-'}</td>
                            <td className="px-4 py-2">{row[customerMapping.round] || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Upload Jobs CSV */}
          {currentStep === 2 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase size={32} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900">Step 2: Upload Jobs</h3>
                <p className="text-sm text-zinc-500 mt-1">Upload your jobs list CSV file (optional)</p>
              </div>

              {!jobFile ? (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-zinc-200 rounded-xl p-8 text-center hover:border-blue-300 transition-colors">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleJobFileChange}
                      className="hidden"
                      id="job-csv-upload"
                    />
                    <label htmlFor="job-csv-upload" className="cursor-pointer">
                      <Upload size={48} className="mx-auto text-zinc-300 mb-4" />
                      <p className="text-zinc-600 font-medium">Drop your Jobs CSV here or click to browse</p>
                      <p className="text-sm text-zinc-400 mt-1">Required: customer_name | Optional: service, price, frequency, next_due</p>
                    </label>
                  </div>
                  
                  <button
                    onClick={() => {
                      setMatchedJobs([]);
                      setUnmatchedJobs([]);
                      setCurrentStep(3);
                    }}
                    className="w-full py-2 text-zinc-600 hover:text-zinc-900"
                  >
                    Skip - Import customers only
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
                    <FileText className="text-blue-600" />
                    <div className="flex-1">
                      <p className="font-medium text-blue-800">{jobFile.name}</p>
                      <p className="text-sm text-blue-600">{jobData.length} jobs found</p>
                    </div>
                    <CheckCircle className="text-blue-500" />
                  </div>

                  {/* Column Mapping */}
                  <div className="bg-zinc-50 rounded-xl p-4">
                    <h4 className="font-medium text-zinc-900 mb-3">Column Mapping</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {['customer_name', 'service', 'price', 'frequency', 'next_due', 'last_done', 'round', 'assignee'].map(col => (
                        <div key={col} className="flex items-center gap-2">
                          <label className="w-28 text-sm font-medium text-zinc-700">
                            {col}{col === 'customer_name' && <span className="text-red-500">*</span>}
                          </label>
                          <select
                            value={jobMapping[col] || ''}
                            onChange={(e) => setJobMapping({...jobMapping, [col]: e.target.value})}
                            className="flex-1 px-3 py-1.5 border border-zinc-200 rounded-lg text-sm"
                          >
                            <option value="">-- Not mapped --</option>
                            {jobHeaders.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="overflow-x-auto border border-zinc-200 rounded-xl">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Customer</th>
                          <th className="px-4 py-2 text-left">Service</th>
                          <th className="px-4 py-2 text-left">Price</th>
                          <th className="px-4 py-2 text-left">Frequency</th>
                          <th className="px-4 py-2 text-left">Round</th>
                          <th className="px-4 py-2 text-left">Next Due</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobData.slice(0, 3).map((row, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-2">{row[jobMapping.customer_name] || '-'}</td>
                            <td className="px-4 py-2">{row[jobMapping.service] || row[jobMapping.services] || '-'}</td>
                            <td className="px-4 py-2">{row[jobMapping.price] || '-'}</td>
                            <td className="px-4 py-2">{row[jobMapping.frequency] || '-'}</td>
                            <td className="px-4 py-2">{row[jobMapping.round] || '-'}</td>
                            <td className="px-4 py-2">{row[jobMapping.next_due] || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review */}
          {currentStep === 3 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Link size={32} className="text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900">Review Import</h3>
                <p className="text-sm text-zinc-500 mt-1">Check the summary before importing</p>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-emerald-50 rounded-xl p-5 text-center">
                  <Users className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
                  <p className="text-3xl font-bold text-emerald-700">{customerData.length}</p>
                  <p className="text-sm text-emerald-600">Customers to import</p>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-5 text-center">
                  <Briefcase className="w-10 h-10 mx-auto text-blue-500 mb-2" />
                  <p className="text-3xl font-bold text-blue-700">{matchedJobs.length}</p>
                  <p className="text-sm text-blue-600">Jobs matched</p>
                </div>
                
                <div className="bg-amber-50 rounded-xl p-5 text-center">
                  <AlertCircle className="w-10 h-10 mx-auto text-amber-500 mb-2" />
                  <p className="text-3xl font-bold text-amber-700">{unmatchedJobs.length}</p>
                  <p className="text-sm text-amber-600">Jobs unmatched (skipped)</p>
                </div>
              </div>

              {unmatchedJobs.length > 0 && (
                <div className="bg-amber-50 rounded-xl p-4 mb-4">
                  <h4 className="font-medium text-amber-800 mb-2">
                    {unmatchedJobs.length} jobs will be skipped (customer not in import list)
                  </h4>
                  <div className="text-sm text-amber-700 max-h-32 overflow-y-auto">
                    {unmatchedJobs.slice(0, 10).map((job, idx) => (
                      <div key={idx}>• {job._customerName || 'Unknown customer'}</div>
                    ))}
                    {unmatchedJobs.length > 10 && (
                      <div className="text-amber-600 mt-1">...and {unmatchedJobs.length - 10} more</div>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-zinc-50 rounded-xl p-4">
                <h4 className="font-medium text-zinc-900 mb-2">What will happen:</h4>
                <ul className="text-sm text-zinc-600 space-y-1">
                  <li>✓ {customerData.length} active customers will be imported</li>
                  {matchedJobs.length > 0 && <li>✓ {matchedJobs.length} jobs will be created and linked to customers</li>}
                  {unmatchedJobs.length > 0 && <li>⚠ {unmatchedJobs.length} jobs will be skipped (no matching customer)</li>}
                  <li>✓ Duplicate customers (by name + address) will be skipped</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 4: Importing */}
          {currentStep === 4 && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-16 h-16 animate-spin text-emerald-600 mb-4" />
              <h3 className="text-xl font-semibold text-zinc-900">Importing your data...</h3>
              <p className="text-sm text-zinc-500 mt-2">This may take a moment for large imports</p>
            </div>
          )}

          {/* Step 5: Complete */}
          {currentStep === 5 && importResult && (
            <div className="text-center py-8">
              <CheckCircle className="w-20 h-20 mx-auto text-emerald-500 mb-4" />
              <h3 className="text-2xl font-semibold text-zinc-900 mb-2">Import Complete!</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mt-8">
                <div className="bg-emerald-50 rounded-xl p-4">
                  <p className="text-3xl font-bold text-emerald-700">{importResult.customersCreated}</p>
                  <p className="text-sm text-emerald-600">New Customers</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-3xl font-bold text-blue-700">{importResult.jobsCreated}</p>
                  <p className="text-sm text-blue-600">New Jobs</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-3xl font-bold text-amber-700">{importResult.jobsUnmatched || 0}</p>
                  <p className="text-sm text-amber-600">Unmatched</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4">
                  <p className="text-3xl font-bold text-red-700">{importResult.jobsSkippedDuplicates || 0}</p>
                  <p className="text-sm text-red-600">Duplicate Jobs Skipped</p>
                </div>
              </div>

              {importResult.jobsSkippedDuplicates > 0 && (
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-lg mx-auto">
                  <p className="text-amber-800 font-medium">
                    {importResult.jobsSkippedDuplicates} duplicate job(s) were skipped
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    These jobs already exist in your account (same customer, service, price, and address).
                  </p>
                </div>
              )}

              <p className="text-zinc-500 mt-6">
                Your customers and jobs have been imported successfully!
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-zinc-100 flex justify-between items-center">
          {currentStep === 1 && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={() => setCurrentStep(2)}
                disabled={!customerFile || !customerMapping.name}
                className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-2"
              >
                Next: Upload Jobs <ArrowRight size={18} />
              </button>
            </>
          )}

          {currentStep === 2 && (
            <>
              <button onClick={() => setCurrentStep(1)} className="px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg">
                Back
              </button>
              <button
                onClick={goToReview}
                className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 flex items-center gap-2"
              >
                Review Import <ArrowRight size={18} />
              </button>
            </>
          )}

          {currentStep === 3 && (
            <>
              <button onClick={() => setCurrentStep(jobFile ? 2 : 1)} className="px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg">
                Back
              </button>
              <button
                onClick={handleImport}
                className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 flex items-center gap-2"
              >
                <Upload size={18} /> Start Import
              </button>
            </>
          )}

          {currentStep === 5 && (
            <button
              onClick={onClose}
              className="ml-auto px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SqueegeeImport;
